import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { FeatureIdentifier, Map } from "mapbox-gl";
import { enqueueSnackbar } from "notistack";

import analytics from "fond/analytics";
import { BASE_MAP_BACKGROUND_OVERLAY } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { baseDrawControlModes } from "fond/draw/helper";
import { MapboxDrawBase, MapStyle } from "fond/types";
import { GeolocationErrorLabel, getGeolocationErrorName } from "fond/types/geolocation";
import localStorage from "fond/utils/localStorage";

import { getMapStyle } from "./styles";
import { defaultToolbarConfig, ToolbarConfig } from "./Toolbar";

export type DrawMode =
  | "direct_select"
  | "simple_select"
  | "draw_point"
  | "draw_polygon"
  | "no_feature"
  | "draw_line_string"
  | "static"
  | "street_view";

/**
 * The MapContext allows us to provide child components with access to the map instance
 * and other common functions used across several components.
 */

type UserPosition = {
  center: number[];
  accuracy: number;
};

export const MapContext = createContext<{
  /**
   * The mapbox gl map instance
   */
  map: Map | undefined;
  mapStyleOpacity?: number;
  setMapStyleOpacity?: (opacity: number) => void;
  drawControl: React.MutableRefObject<MapboxDrawBase | null>;
  toolbar: React.MutableRefObject<ToolbarConfig>;
  /**
   * Flag indicating is the current draw session is dirty (create / update events fired)
   */
  isDirty: boolean;
  /**
   * Flag indicating if the map is currently in drawing mode.
   */
  isDrawing: boolean;
  setIsDirty(dirty: boolean): void;
  setMap(map?: Map): void;
  /**
   * Sets the draw mode on the MapboxDraw control.  Setting the draw mode to "no_feature"
   * will exit drawing mode and remove any current features being drawn.
   */
  setDrawMode(mode: string, options?: object): void;
  /**
   * Handles setting the features state of the features passed.  Commonly used to
   * highlight features on selection.
   */
  setFeatureState({ features, state }: { features: FeatureIdentifier[] | mapboxgl.MapboxGeoJSONFeature[]; state: any }): void;
  /**
   * Values needed for the display of user location on map
   */
  userPosition: UserPosition | null;
  startPositionWatch: () => void;
  stopPositionWatch: () => void;
  /**
   * The style of the map's background.
   *
   * One of map (default), satellite, hybrid, or monochrome.
   */
  mapStyle: MapStyle;
  /**
   * Set a new map background style.
   */
  setMapStyle: (style: MapStyle) => void;
}>(undefined!);

interface IProps {
  children: React.ReactNode;
  mapStyle?: MapStyle;
}

const MapProvider: React.FC<IProps> = ({ children, mapStyle }: IProps) => {
  const [map, setMap] = useState<Map | undefined>(undefined);
  const [style, setStyle] = useState<MapStyle>(mapStyle || "map");
  const [mapStyleOpacity, setMapStyleOpacity] = useState<number>(parseFloat(localStorage.getItem(BASE_MAP_BACKGROUND_OVERLAY, "1")));
  const drawControlRef = useRef<MapboxDrawBase | null>(null);
  const toolbarRef = useRef(defaultToolbarConfig);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [userPosition, setUserPosition] = useState<UserPosition | null>(null);
  const positionWatchId = useRef<number>();

  /**
   * Sets the draw type mode
   */
  const setDrawMode = useCallback(
    (mode: DrawMode, options?: object) => {
      if (mode === "no_feature") {
        if (drawControlRef.current && map?.hasControl(drawControlRef.current)) {
          // No drawing control required for this mode

          // We change to simple select mode prior to removing the control so that
          // any drawing modes call their related onStop methods to clean up event listeners
          // and other resources.
          drawControlRef.current.changeMode("simple_select");
          drawControlRef.current.deleteAll();
          map?.removeControl(drawControlRef.current);
          setIsDrawing(false);
          setIsDirty(false);
        }
      } else {
        // Add the control if it does not already exist
        if (!drawControlRef.current) {
          // instantiate the default draw control
          drawControlRef.current = new MapboxDraw({
            displayControlsDefault: false,
            modes: baseDrawControlModes,
            styles: defaultStyles,
          });
        }

        if (drawControlRef.current && !map?.hasControl(drawControlRef.current)) {
          map?.addControl(drawControlRef.current);
        }

        // Change the mode to the required drawing mode
        drawControlRef.current?.changeMode(mode as string, options);
        setIsDrawing(true);
      }
    },
    [map]
  );

  /**
   * Handles setting the features states
   * For example a featureState { isSelected: true } will hightlight the features
   * (based on mapbox styles)
   */
  const setFeatureState = useCallback(
    ({ features, state }: { features: FeatureIdentifier[] | mapboxgl.MapboxGeoJSONFeature[]; state: any }) => {
      features.forEach((feature) => {
        map?.setFeatureState(
          {
            source: feature.source,
            sourceLayer: feature.sourceLayer,
            id: feature.id,
          },
          state
        );
      });
    },
    [map]
  );

  /**
   * Handles getting of user location and setting of its watcher
   */
  const startPositionWatch = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported");
      enqueueSnackbar(GeolocationErrorLabel.NO_SUPPORT);
      return;
    }

    const handleLocationSuccess = ({ coords: { longitude, latitude, accuracy } }: GeolocationPosition) => {
      setUserPosition({ center: [longitude, latitude], accuracy: accuracy * 0.001 });
    };

    const handleLocationError = (err: GeolocationPositionError) => {
      const errorName = getGeolocationErrorName(err.code);
      // Ignore timeout errors
      if (err.code !== 3) {
        enqueueSnackbar(`Error: ${GeolocationErrorLabel[errorName]}`);
      }
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationSuccess(position);
        map?.flyTo({
          center: [position.coords.longitude, position.coords.latitude],
          zoom: Math.max(15, map?.getZoom()),
        });
      },
      handleLocationError,
      { maximumAge: 60000, timeout: 5000 }
    );

    positionWatchId.current = navigator.geolocation.watchPosition(handleLocationSuccess, handleLocationError, {
      maximumAge: 10000,
      enableHighAccuracy: true,
    });
    analytics.track("map.controls.current-location.started");
  }, [map]);

  /**
   * Stops tracking of user location
   */
  const stopPositionWatch = useCallback(() => {
    if (typeof positionWatchId.current !== "undefined") {
      navigator.geolocation.clearWatch(positionWatchId.current);
      setUserPosition(null);
      analytics.track("map.controls.current-location.stopped");
    }
  }, []);

  /**
   * Set the map style opacity
   */
  const handleMapStyleOpacityChange = useCallback(
    (opacity: number) => {
      setMapStyleOpacity(opacity);
      map?.setPaintProperty(BASE_MAP_BACKGROUND_OVERLAY, "background-opacity", 1 - opacity);
      map?.setPaintProperty(BASE_MAP_BACKGROUND_OVERLAY, "background-opacity-transition", { duration: 0, delay: 0 });
      localStorage.setItem(BASE_MAP_BACKGROUND_OVERLAY, opacity.toString());
    },
    [map]
  );

  /**
   * Set a new map style.
   */
  const setMapStyle = useCallback(
    (newMapStyle: MapStyle) => {
      if (map) {
        setStyle(newMapStyle);
        map.setStyle(getMapStyle(newMapStyle).url);
        stopPositionWatch();

        // Re-apply map style opacity once the style has loaded
        map?.once("styledata", () => {
          handleMapStyleOpacityChange(mapStyleOpacity);
        });
      }
    },
    [handleMapStyleOpacityChange, map, mapStyleOpacity, stopPositionWatch]
  );

  const value = useMemo(
    () => ({
      map,
      drawControl: drawControlRef,
      isDrawing,
      isDirty,
      mapStyle: style,
      mapStyleOpacity,
      setMapStyleOpacity: handleMapStyleOpacityChange,
      setDrawMode,
      setFeatureState,
      setIsDirty,
      setMap,
      setMapStyle,
      startPositionWatch,
      stopPositionWatch,
      toolbar: toolbarRef,
      userPosition,
    }),
    [
      map,
      isDrawing,
      isDirty,
      style,
      mapStyleOpacity,
      handleMapStyleOpacityChange,
      setDrawMode,
      setFeatureState,
      setMapStyle,
      startPositionWatch,
      stopPositionWatch,
      userPosition,
    ]
  );

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

export default MapProvider;
